// useAppConfig — read several public.app_config keys in one request.
//
// `value` is raw JSONB (boolean, number, string or array); callers coerce.
// The client's default schema is `studio`, so the read names `public`
// explicitly. Admins can read every key (RLS: is_admin()).

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '../queryKeys';

export type AppConfigValues = Readonly<Record<string, unknown>>;

export function useAppConfig(keys: readonly string[]): UseQueryResult<AppConfigValues, Error> {
  return useQuery({
    queryKey: queryKeys.appConfig.values(keys),
    queryFn: async () => {
      if (keys.length === 0) return {};
      const { data, error } = await supabase
        .schema('public')
        .from('app_config')
        .select('key, value')
        .in('key', [...keys]);
      if (error) throw error;
      const out: Record<string, unknown> = {};
      for (const row of (data ?? []) as { key: string; value: unknown }[]) out[row.key] = row.value;
      return out;
    },
  });
}

/** Coercion helpers for raw JSONB values. */
export const configValue = {
  bool(v: unknown, fallback: boolean): boolean {
    return typeof v === 'boolean' ? v : fallback;
  },
  num(v: unknown, fallback: number): number {
    return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
  },
  str(v: unknown, fallback = ''): string {
    return typeof v === 'string' ? v : fallback;
  },
  strings(v: unknown): string[] {
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  },
};
